/* eslint react/jsx-props-no-spreading: 0 */
import {
    Backdrop,
    Box,
    CircularProgress,
    Divider,
    MobileStepper,
    Stack,
    Step,
    StepLabel,
    Stepper,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
    useForm,
    SubmitHandler,
    UseFormRegister,
    Control,
    useFormState,
    FieldErrors,
    FormProvider,
    DefaultValues,
} from 'react-hook-form';

import { FC, useState, useCallback, ComponentProps, ReactNode, useRef } from 'react';
import { Button, FormAlert } from '../../ui';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';

function Layout({
    children,
    header,
    containerSx,
    cancelButton,
    backButton,
    nextButton,
    maxSteps,
    currentStep,
    stepTitle,
}: {
    children: ReactNode;
    header: ReactNode;
    cancelButton: (isMobile: boolean) => ReactNode;
    backButton: (isMobile: boolean) => ReactNode;
    nextButton: (isMobile: boolean) => ReactNode;
    containerSx?: SxProps<Theme>;
    maxSteps: number;
    currentStep: number;
    stepTitle: string;
}) {
    const theme = useTheme();

    const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

    if (isSmDown) {
        return (
            <Stack
                sx={[
                    {
                        backgroundColor: 'background.paper',
                        height: '100%',
                        width: '100%',
                        justifyContent: 'space-between',
                        position: 'relative',
                    },
                    ...(Array.isArray(containerSx) ? containerSx : [containerSx]),
                ]}
            >
                <Stack sx={{ overflowY: 'auto' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            height: 50,
                            pl: 2,
                        }}
                    >
                        <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
                            <Typography
                                variant="caption"
                                paddingRight={1}
                                sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: '1',
                                    WebkitBoxOrient: 'vertical',
                                }}
                            >
                                {stepTitle}
                            </Typography>
                            {cancelButton(isSmDown)}
                        </Stack>
                    </Box>
                    <Divider />
                    <Stack data-testid="form-header" spacing={1} p={1}>
                        {header}
                    </Stack>
                    {children}
                </Stack>
                <Stack
                    sx={{
                        position: 'sticky',
                        bottom: 0,
                        backgroundColor: 'grey.100',
                    }}
                >
                    <Divider />
                    <MobileStepper
                        sx={{
                            backgroundColor: 'transparent',
                        }}
                        variant="dots"
                        position="static"
                        steps={maxSteps}
                        activeStep={currentStep}
                        backButton={backButton(isSmDown)}
                        nextButton={nextButton(isSmDown)}
                    />
                </Stack>
            </Stack>
        );
    }

    return (
        <Stack
            data-testid="form-container"
            spacing={4}
            sx={[
                {
                    backgroundColor: 'background.paper',
                    boxShadow: (t) => t.shadows[1],
                    height: '100%',
                    width: '100%',
                    maxWidth: (t) => t.breakpoints.values.lg,
                    justifyContent: 'space-between',
                    overflowY: 'auto',
                    position: 'relative',
                },
                ...(Array.isArray(containerSx) ? containerSx : [containerSx]),
            ]}
        >
            <Stack spacing={4}>
                <Stack data-testid="form-header" spacing={4}>
                    {header}
                </Stack>
                {children}
            </Stack>
            <Stack data-testid="form-footer" direction="row" alignItems="center" spacing={2} justifyContent="flex-end">
                {cancelButton(isSmDown)}
                <Stack direction="row" spacing={2}>
                    {backButton(isSmDown)}
                    {nextButton(isSmDown)}
                </Stack>
            </Stack>
        </Stack>
    );
}

export type FormStepComponentProps<TSchema extends z.ZodTypeAny, TFields = z.infer<TSchema>> = {
    register: UseFormRegister<TFields>;
    control: Control<TFields, any, TFields>;
};

function FormStepper<T>({
    steps,
    activeStep,
    fields,
    control,
    isValidStep,
}: {
    activeStep: number;
    isValidStep: (errors: FieldErrors<T>) => boolean;
    steps: { id: number; title: string }[];
    fields: (keyof T)[];
    control: Control<T, any, T>;
}) {
    const { errors } = useFormState({ name: fields as any[], control });

    const hasErrors = !isValidStep(errors);

    return (
        <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((step, idx) => {
                const isActiveStep = idx === activeStep;
                return (
                    <Step key={step.id}>
                        <StepLabel error={isActiveStep && hasErrors}>
                            <Typography variant={isActiveStep ? 'subtitle2' : 'body1'}>{step.title}</Typography>
                        </StepLabel>
                    </Step>
                );
            })}
        </Stepper>
    );
}

/**
 * This component is subscribed to form state updates
 * and it will disable the next/submit button if there are any errors
 * in the fields for the current step
 */
function SmartNextButton<T>({
    next,
    isValidStep,
    children,
    fields,
    control,
    variant,
    disabled = false,
}: {
    next: () => void;
    isValidStep: (errors: FieldErrors<T>) => boolean;
    children: ReactNode;
    fields: (keyof T)[];
    control: Control<T, any, T>;
    disabled?: boolean;
    variant?: 'text' | 'default';
}) {
    const { errors } = useFormState({ name: fields as any[], control });

    const nextIsDisabled = !isValidStep(errors) || disabled;

    return (
        <Button testid="smart-next-button" onClick={next} disabled={nextIsDisabled} variant={variant}>
            {children}
        </Button>
    );
}

type FormContext = {
    stepIndex: number;
    isLastStep: boolean;
};

interface MultiStepFormProps<TSchema extends z.ZodTypeAny, TFields = z.infer<TSchema>> {
    schema: TSchema;
    submitHandler: SubmitHandler<TFields>;
    steps: {
        /**
         * unique identifier for rendering
         * used as value for the {key} property
         */
        id: number;
        title: string;
        component: FC<FormStepComponentProps<TSchema, TFields>>;
        fields: (keyof TFields)[];
    }[];
    onCancel?: (reset: () => void) => void;
    containerSx?: SxProps<Theme>;
    formAlertProps?: Pick<
        ComponentProps<typeof FormAlert>,
        'errorMessage' | 'loadingMessage' | 'successMessage' | 'savingMessage' | 'warningMessage'
    >;
    defaultValues?: DefaultValues<TFields>;
    values?: TFields;
    shouldDisableNext?: (ctx: FormContext) => boolean;
    shouldDisableBack?: (ctx: FormContext) => boolean;
    shouldDisableCancel?: (ctx: FormContext) => boolean;
    isLoading?: boolean;
    /**
     * Optional async callback invoked after the current step's fields pass
     * validation but *before* the form advances to the next step.
     *
     * Return `true` to proceed, or `false` to stay on the current step
     * (e.g. when a per-step API call fails and the user should fix / retry).
     *
     * Not called on the final step — use `submitHandler` for that.
     */
    onBeforeNext?: (stepIndex: number, formData: TFields) => Promise<boolean>;
    /**
     * Optional callback invoked when final submission is blocked by
     * validation errors. Without it, a failed final validation is silent —
     * intermediate steps only gate on their own fields, so the form can
     * reach the last step while fields on other steps are still invalid.
     */
    onInvalidSubmit?: (errors: FieldErrors<TFields>) => void;
    /**
     * Optional callback invoked whenever the visible step changes (Next,
     * Previous, or reset). Useful for clearing step-scoped messages so
     * they don't follow the user to another step.
     */
    onStepChange?: (stepIndex: number) => void;
}

export function MultiStepForm<TSchema extends z.ZodTypeAny, TFields = z.infer<TSchema>>({
    schema,
    submitHandler,
    steps,
    onCancel,
    containerSx,
    formAlertProps,
    defaultValues,
    shouldDisableNext,
    shouldDisableBack,
    shouldDisableCancel,
    isLoading = false,
    values,
    onBeforeNext,
    onInvalidSubmit,
    onStepChange,
}: MultiStepFormProps<TSchema, TFields>) {
    const methods = useForm<TFields>({
        resolver: zodResolver(schema),
        mode: 'onBlur',
        defaultValues: defaultValues,
        values,
    });
    const topRef = useRef<HTMLDivElement | null>(null);

    const { control, register, handleSubmit, reset, clearErrors } = methods;

    const [currentStep, setCurrentStep] = useState(0);

    const isLastStep = currentStep === steps.length - 1;
    const stepConfig = steps[currentStep];
    const CurrentStepComponent = stepConfig.component;
    const stepFields = stepConfig.fields;

    const isValidStep = useCallback(
        (errors: FieldErrors<TFields>) => stepFields.every((field) => errors[field] == null),
        [stepFields]
    );

    const scrollToTop = () => {
        topRef.current?.scrollIntoView();
    };

    const next = useCallback(async () => {
        if (isLastStep) {
            scrollToTop();
            await handleSubmit(submitHandler, onInvalidSubmit)();
        }

        if (currentStep < steps.length - 1) {
            handleSubmit(
                async (formData) => {
                    if (onBeforeNext) {
                        const shouldProceed = await onBeforeNext(currentStep, formData);
                        if (!shouldProceed) {
                            return;
                        }
                    }
                    setCurrentStep((step) => step + 1);
                    onStepChange?.(currentStep + 1);
                },
                async (errors) => {
                    const isValid = isValidStep(errors);

                    if (!isValid) {
                        return;
                    }

                    if (onBeforeNext) {
                        const formData = methods.getValues();
                        const shouldProceed = await onBeforeNext(currentStep, formData);
                        if (!shouldProceed) {
                            return;
                        }
                    }

                    setCurrentStep((step) => step + 1);
                    onStepChange?.(currentStep + 1);
                    clearErrors();
                }
            )();
        }
    }, [
        currentStep,
        isLastStep,
        steps.length,
        handleSubmit,
        submitHandler,
        clearErrors,
        isValidStep,
        onBeforeNext,
        onInvalidSubmit,
        onStepChange,
        methods,
    ]);

    const goto = useCallback(
        (stepIdx) => {
            if (stepIdx > -1 && stepIdx < steps.length) {
                setCurrentStep(stepIdx);
                onStepChange?.(stepIdx);
            }
        },
        [steps.length, onStepChange]
    );

    const prev = useCallback(() => {
        goto(currentStep - 1);
    }, [goto, currentStep]);

    const formReset = useCallback(() => {
        goto(0);
        reset();
    }, [reset, goto]);

    const cancel = useCallback(() => {
        if (!onCancel) {
            formReset();
            return;
        }

        onCancel?.(formReset);
    }, [formReset, onCancel]);

    const formContext: FormContext = {
        stepIndex: currentStep,
        isLastStep,
    };

    const disableNext = shouldDisableNext?.(formContext) ?? false;
    const disableBack = shouldDisableBack?.(formContext) ?? false;
    const disableCancel = shouldDisableCancel?.(formContext) ?? false;

    return (
        <Layout
            containerSx={containerSx}
            maxSteps={steps.length}
            currentStep={currentStep}
            stepTitle={stepConfig.title}
            header={
                <>
                    <Box
                        ref={topRef}
                        width="100%"
                        data-testid="stepper-container"
                        sx={{
                            display: {
                                xs: 'none',
                                sm: 'block',
                            },
                        }}
                    >
                        <FormStepper
                            steps={steps}
                            activeStep={currentStep}
                            control={control}
                            fields={stepFields}
                            isValidStep={isValidStep}
                        />
                    </Box>
                    <FormAlert
                        testId="form-message"
                        errorMessage={formAlertProps?.errorMessage ?? null}
                        savingMessage={formAlertProps?.savingMessage ?? null}
                        loadingMessage={formAlertProps?.loadingMessage ?? null}
                        successMessage={formAlertProps?.successMessage ?? null}
                        infoMessage={null}
                        warningMessage={formAlertProps?.warningMessage ?? null}
                    />
                </>
            }
            cancelButton={() => (
                <Button onClick={cancel} variant="text" disabled={disableCancel}>
                    Cancel
                </Button>
            )}
            backButton={(isMobile) => (
                <Button
                    onClick={prev}
                    disabled={disableBack || currentStep < 1}
                    variant={isMobile ? 'text' : 'secondary'}
                >
                    <KeyboardArrowLeft />
                    {isMobile ? 'Back' : 'Previous'}
                </Button>
            )}
            nextButton={(isMobile) => (
                <SmartNextButton
                    variant={isMobile && !isLastStep ? 'text' : 'default'}
                    fields={stepFields}
                    control={control}
                    isValidStep={isValidStep}
                    next={next}
                    disabled={disableNext}
                >
                    {isLastStep ? (
                        'Submit'
                    ) : (
                        <>
                            Next
                            <KeyboardArrowRight />
                        </>
                    )}
                </SmartNextButton>
            )}
        >
            <Backdrop
                open={isLoading}
                sx={{
                    position: 'absolute',
                    zIndex: 1,
                }}
            >
                <CircularProgress />
            </Backdrop>
            <FormProvider {...methods}>
                <CurrentStepComponent register={register} control={control} />
            </FormProvider>
        </Layout>
    );
}
